/**
 * BiasInsightCard
 *
 * Renders a single detected cognitive bias with headline, illustration,
 * explanation, and mitigation. Expandable — headline is always visible,
 * details unfold on click. Severity determines border/accent color.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, ShieldAlert, Info, BookOpen } from 'lucide-react';
import type { DetectedBias } from '../../../types/prioritization/cognitiveBias';
import { BIAS_META, BIAS_CATEGORY_META } from '../../../types/prioritization/cognitiveBias';

interface BiasInsightCardProps {
  bias: DetectedBias;
  defaultExpanded?: boolean;
}

const SEVERITY_STYLES: Record<string, { border: string; badge: string; icon: string }> = {
  high:   { border: 'border-[var(--coral)]',    badge: 'bg-[var(--coral-dim)] text-[var(--coral)]',    icon: 'text-[var(--coral)]' },
  medium: { border: 'border-[var(--mustard)]',  badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', icon: 'text-[var(--mustard)]' },
  low:    { border: 'border-[var(--slate)]',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',   icon: 'text-[var(--text-ter)]' },
};

const SEVERITY_ICON = {
  high:   ShieldAlert,
  medium: AlertTriangle,
  low:    Info,
};

const CATEGORY_BADGE_STYLES: Record<string, string> = {
  group_dynamics:      'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
  cognitive_shortcuts: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]',
  strategic_framing:   'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]',
  confidence:          'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
};

function ConfidencePip({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  return (
    <div className="flex items-center gap-1.5" title={`AI confidence: ${pct}%`}>
      <div className="relative h-1.5 w-16 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-[var(--surface)] rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-[var(--text-ter)] tabular-nums">{pct}%</span>
    </div>
  );
}

export function BiasInsightCard({ bias, defaultExpanded = false }: BiasInsightCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const meta = BIAS_META[bias.biasType];
  const catMeta = BIAS_CATEGORY_META[meta.category];
  const styles = SEVERITY_STYLES[bias.severity] ?? SEVERITY_STYLES.medium;
  const SeverityIcon = SEVERITY_ICON[bias.severity] ?? Info;

  return (
    <div className={`bg-[var(--bg-raised)]  border ${styles.border} overflow-hidden`}>
      {/* ── Header (always visible) ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-[var(--surface)] transition-colors"
        aria-expanded={expanded}
      >
        <SeverityIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${styles.icon}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-[var(--text-pri)]">{meta.label}</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${CATEGORY_BADGE_STYLES[meta.category]}`}>
              {catMeta.label}
            </span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${styles.badge}`}>
              {bias.severity} risk
            </span>
          </div>
          <p className="text-sm text-[var(--text-sec)] leading-snug">{bias.headline}</p>
        </div>
        <div className="flex-shrink-0 mt-0.5">
          {expanded
            ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
            : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          }
        </div>
      </button>

      {/* ── Expanded detail ─────────────────────────────────────────────── */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-[var(--rule)]">

          {/* Illustration — the "that's us" moment */}
          {bias.illustration && (
            <div className="mt-4 bg-[var(--surface)] p-4">
              <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5">
                In this context
              </p>
              <p className="text-sm text-[var(--text-sec)] leading-relaxed italic">
                "{bias.illustration}"
              </p>
            </div>
          )}

          {/* Explanation */}
          {bias.explanation && (
            <div>
              <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5">
                Why it matters
              </p>
              <p className="text-sm text-[var(--text-sec)] leading-relaxed">{bias.explanation}</p>
            </div>
          )}

          {/* Mitigation */}
          {bias.mitigation && (
            <div className="bg-primary-50 border border-primary-100 px-4 py-3">
              <p className="text-[10px] font-semibold text-primary-600 uppercase tracking-wide mb-1">
                Countermeasure
              </p>
              <p className="text-sm text-primary-900 leading-relaxed">{bias.mitigation}</p>
            </div>
          )}

          {/* Kahneman framing */}
          <div className="flex items-start gap-2 pt-1">
            <BookOpen className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-[var(--text-ter)] leading-relaxed italic">
              {meta.kahneman}
            </p>
          </div>

          {/* Evidence items */}
          {bias.evidenceItemTitles && bias.evidenceItemTitles.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5">
                Signal items
              </p>
              <div className="flex flex-wrap gap-1.5">
                {bias.evidenceItemTitles.map(title => (
                  <span
                    key={title}
                    className="inline-flex items-center bg-[var(--hover-tint)] px-2.5 py-1 text-xs text-[var(--text-sec)]"
                  >
                    {title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Confidence pip */}
          <div className="flex items-center justify-end pt-1">
            <ConfidencePip confidence={bias.confidence} />
          </div>
        </div>
      )}
    </div>
  );
}
